import { supabase } from "./client";
import { getCurrentEmpresaId } from "./auth-utils";

/**
 * 🧹 FUNÇÃO DE LIMPEZA: Remove items duplicados da tabela atelie_order_items
 *
 * Identifica duplicatas baseada em (order_id + description + quantity + unit_value)
 * e mantém apenas a mais antiga (primeira inserida).
 */
export async function cleanupDuplicateOrderItems(): Promise<number> {
  try {
    console.log("🧹 [CLEANUP_ITEMS] INICIANDO limpeza de items duplicados...");

    const empresa_id = await getCurrentEmpresaId();

    // Buscar TODOS os items
    const { data: allItems, error: fetchError } = await (supabase
      .from("atelie_order_items" as any)
      .select("id, order_id, description, quantity, unit_value, created_at")
      .eq("empresa_id", empresa_id)
      .order("created_at", { ascending: true }) as any);

    if (fetchError) {
      console.error("❌ [CLEANUP_ITEMS] Erro ao buscar:", fetchError.message);
      return 0;
    }

    if (!allItems || allItems.length === 0) {
      console.log("✅ [CLEANUP_ITEMS] Nenhum item encontrado");
      return 0;
    }

    console.log(`📊 [CLEANUP_ITEMS] Total de items no banco: ${allItems.length}`);

    // Agrupar por (order_id + description + quantity + unit_value) para encontrar duplicatas
    const grouped = allItems.reduce((acc: any, item: any) => {
      const key = `${item.order_id}||${item.description}||${item.quantity}||${item.unit_value}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    let totalRemoved = 0;

    // Para cada chave, se houver mais de 1, remover os duplicados (manter o primeiro)
    for (const [key, items] of Object.entries(grouped)) {
      const itemsArray = items as any[];
      if (itemsArray.length > 1) {
        // Manter o primeiro (mais antigo), remover o resto
        const toKeep = itemsArray[0];
        const toDelete = itemsArray.slice(1);

        console.log(`🔴 [CLEANUP_ITEMS] Encontradas ${itemsArray.length} cópias de: ${toKeep.description} (qtd: ${toKeep.quantity})`);
        console.log(`   Mantendo: ID ${toKeep.id} (criado em ${toKeep.created_at})`);

        for (const item of toDelete) {
          const { error: delError } = await (supabase
            .from("atelie_order_items" as any)
            .delete()
            .eq("id", item.id) as any);

          if (!delError) {
            totalRemoved++;
            console.log(`   ✅ Removido: ID ${item.id}`);
          } else {
            console.error(`   ❌ Erro ao remover ${item.id}:`, delError);
          }
        }
      }
    }

    console.log(`\n✅ [CLEANUP_ITEMS] Limpeza de items concluída! ${totalRemoved} registros removidos.`);
    return totalRemoved;
  } catch (error) {
    console.error("❌ [CLEANUP_ITEMS] Erro fatal:", error);
    return 0;
  }
}

/**
 * 🧹 FUNÇÃO DE LIMPEZA: Remove personalizations duplicadas do banco
 *
 * Use no console do navegador:
 * window.__cleanupDuplicates()
 *
 * Identifica duplicatas baseada em (person_name + size + quantity + notes)
 * e mantém apenas a mais antiga (primeira inserida).
 */
export async function cleanupDuplicatePersonalizations(): Promise<number> {
  try {
    console.log("🧹 [CLEANUP] INICIANDO limpeza de duplicatas...");
    
    const empresa_id = await getCurrentEmpresaId();
    
    // Buscar TODAS as personalizations
    const { data: allPersonalizations, error: fetchError } = await (supabase
      .from("atelie_order_personalizations" as any)
      .select("id, order_id, person_name, size, quantity, notes, created_at")
      .eq("empresa_id", empresa_id)
      .order("created_at", { ascending: true }) as any);

    if (fetchError) {
      console.error("❌ [CLEANUP] Erro ao buscar:", fetchError.message);
      return 0;
    }

    if (!allPersonalizations || allPersonalizations.length === 0) {
      console.log("✅ [CLEANUP] Nenhuma personalização encontrada");
      return 0;
    }

    console.log(`📊 [CLEANUP] Total de personalizations no banco: ${allPersonalizations.length}`);

    // Agrupar por (person_name + size + quantity + notes) para encontrar duplicatas
    const grouped = allPersonalizations.reduce((acc: any, p: any) => {
      const key = `${p.person_name}||${p.size}||${p.quantity}||${p.notes}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(p);
      return acc;
    }, {});

    let totalRemoved = 0;

    // Para cada chave, se houver mais de 1, remover os duplicados (manter o primeiro)
    for (const [key, items] of Object.entries(grouped)) {
      const itemsArray = items as any[];
      if (itemsArray.length > 1) {
        // Manter o primeiro (mais antigo), remover o resto
        const toKeep = itemsArray[0];
        const toDelete = itemsArray.slice(1);

        console.log(`🔴 [CLEANUP] Encontradas ${itemsArray.length} cópias de: ${key}`);
        console.log(`   Mantendo: ID ${toKeep.id} (criado em ${toKeep.created_at})`);

        for (const item of toDelete) {
          const { error: delError } = await (supabase
            .from("atelie_order_personalizations" as any)
            .delete()
            .eq("id", item.id) as any);

          if (!delError) {
            totalRemoved++;
            console.log(`   ✅ Removido: ID ${item.id}`);
          } else {
            console.error(`   ❌ Erro ao remover ${item.id}:`, delError);
          }
        }
      }
    }

    console.log(`\n✅ [CLEANUP] Limpeza concluída! ${totalRemoved} registros removidos.`);
    return totalRemoved;
  } catch (error) {
    console.error("❌ [CLEANUP] Erro fatal:", error);
    return 0;
  }
}

// Expor função globalmente para uso no console
if (typeof window !== 'undefined') {
  (window as any).__cleanupDuplicates = async () => {
    console.log("🧹 Iniciando limpeza completa de duplicatas...\n");

    const removedPersonalizations = await cleanupDuplicatePersonalizations();
    console.log("\n---\n");

    const removedItems = await cleanupDuplicateOrderItems();

    const totalRemoved = removedPersonalizations + removedItems;
    console.log(`\n🎉 Resultado final: ${totalRemoved} duplicatas removidas no total`);
    if (totalRemoved > 0) {
      console.log("🔄 Recarregando página em 2 segundos...");
      setTimeout(() => window.location.reload(), 2000);
    }
    return totalRemoved;
  };

  console.log("🧹 Função de limpeza disponível! Use: window.__cleanupDuplicates()");
}
